import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../store';
import { firebase, firestore, firestoreQuery, functions } from '../firebase';
import { UserReport } from '../types';
import {actions as userActions} from './users';



interface Filters {
  reason?: string,
  status?: string,
}

interface ReportedUsersState {
  list: UserReport[];
  loading: boolean;
  loadingMore: boolean;
  cursor?: any;
  filters: Filters;
  pendingResolve: {
    [ reportId: string ]: boolean;
  },
  pendingDisable: {
    [ reportId: string ]: boolean;
  }
}

const initialState: ReportedUsersState = {
  list: [],
  loading: false,
  loadingMore: false,
  filters: {},
  pendingDisable: {},
  pendingResolve: {},
}



const filtersChanged = (a: Filters, b: Filters) => {
  return a.reason !== b.reason || a.status !== b.status;
}

const service = createSlice({
  name: 'reportedUsers',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>): ReportedUsersState {
      return { ...state, loading: action.payload, }
    },
    setLoadingMore(state, action: PayloadAction<boolean>): ReportedUsersState {
      return { ...state, loadingMore: action.payload, }
    },
    setList(state, action: PayloadAction<UserReport[]>): ReportedUsersState {
      return {
        ...state,
        list: [
          ...state.list,
          ...action.payload,
        ]
      }
    },
    resetList(state): ReportedUsersState {
      return {
        ...state,
        list: []
      }
    },
    setCursor(state, action: PayloadAction<any>): ReportedUsersState {
      return {
        ...state,
        cursor: action.payload,
      }
    },
    setFilters(state, action: PayloadAction<Filters>): ReportedUsersState {
      return {
        ...state,
        filters: { ...action.payload },
      }
    },
    setPendingResolve(state, action: PayloadAction<{ id: string, value: boolean }>): ReportedUsersState {
      return {
        ...state,
        pendingResolve: {
          ...state.pendingResolve,
          [ action.payload.id ]: action.payload.value,
        }
      }
    },
    setPendingDisable(state, action: PayloadAction<{ id: string, value: boolean }>): ReportedUsersState {
      return {
        ...state,
        pendingDisable: {
          ...state.pendingDisable,
          [ action.payload.id ]: action.payload.value,
        }
      }
    },
    markAsResolved(state, action: PayloadAction<number>): ReportedUsersState {
      const list = [...state.list];
      list[action.payload] = {
        ...list[action.payload],
        status: 'resolved',
      }
      return {
        ...state,
        list,
      }
    },
  }
});

const markAsResolved = (id: string): AppThunk => async (dispatch, getState) => {
  var adminId = getState().auth.user?.id;
  try {
    dispatch(service.actions.setPendingResolve({
      id,
      value: true,
    }));
    await firestore.collection('user_reports').doc(id).update({
      status: 'resolved',
      resolveTime: firebase.firestore.FieldValue.serverTimestamp(),
      adminId,
    });
    dispatch(service.actions.setPendingResolve({
      id,
      value: false,
    }));
    const index = getState().reportedUsers.list.findIndex((item) => item.id === id);

    dispatch(service.actions.markAsResolved(index));

  }
  catch (e) {
    console.error(e);
  }
}


const disableUser = (id: string, userId: string): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(service.actions.setPendingDisable({
      id,
      value: true,
    }));

    const func = functions.httpsCallable('adminDisableUser');

    await func({ userId });

    dispatch(service.actions.setPendingDisable({
      id,
      value: false,
    }));

    dispatch(userActions.updateUserStatus({
      userId,
      status: 'disabled'
    }))

  }
  catch (e) {
    console.error(e);
  }
}

  const loadList = (filters: Filters, loadingMore: boolean): AppThunk => async (dispatch, getState) => {
    const state = getState().reportedUsers;
    let cursor = state.cursor;

    if (!filtersChanged(filters, state.filters) && !loadingMore) {
      return;
    }

    if (loadingMore && !cursor) {
      return;
    }

    console.log('load photos', filters, `loadMore: ${loadingMore}`);

    if (filtersChanged(filters, state.filters)) {
      dispatch(service.actions.resetList());
      dispatch(service.actions.setFilters(filters));
      dispatch(service.actions.setCursor(null));
      cursor = null;
    }

    if (loadingMore) {
      dispatch(service.actions.setLoadingMore(true));
    }
    else {
      dispatch(service.actions.setLoading(true));
    }

    let query = firestore.collection('user_reports').orderBy('createTime', 'desc').limit(50);
    if (cursor) {
      query = query.startAfter(cursor);
    }

    if (filters.status !== 'all') {
      query = query.where('status', '==', filters.status);
    }
    if (filters.reason !== 'all') {
      query = query.where('reason', '==', filters.reason);
    }

    const result = await firestoreQuery<UserReport>(query);

    dispatch(service.actions.setCursor(result.lastItem));
    dispatch(service.actions.setList(result.list));

    if (loadingMore) {
      dispatch(service.actions.setLoadingMore(false));
    }
    else {
      dispatch(service.actions.setLoading(false));
    }

  }




  export const actions = {
    loadList,
    markAsResolved,
    disableUser,
  }

  export default service.reducer;